class rSpinner extends HTMLElement {
    get step() {
        return parseFloat(this.input.getAttribute('step')) || 1;
    }

    get decimalPlaces() {
        if (Math.floor(this.step) === this.step) {
            return 0;
        }

        return this.step.toString().split(".")[1].length || 0;
    }

    get canHaveZero() {
        return this.min <= 0;
    }

    get valueOrMin() {
        let value = this.getParsedValue()

        if (
            isNaN(value)
            || !this.canHaveZero && !value
        ) {
            value = this.min
        }

        return value
    }

    get min() {
        let attrMin = this.input.hasAttribute('min') ? parseFloat(this.input.getAttribute('min')) : 1,
            min = Math.round(attrMin / this.step) * this.step;

        if (min < attrMin) {
            min = attrMin + this.step;
        }

        if (this.decimalPlaces) {
            min = parseFloat(min).toFixed(this.decimalPlaces);
        }

        return parseFloat(min);
    }

    get max() {
        let attrMax = parseFloat(this.input.getAttribute('max')) || 999,
            max = Math.round(attrMax / this.step) * this.step;

        if (max > attrMax) {
            max = attrMax - this.step;
        }

        if (this.decimalPlaces) {
            max = parseFloat(max).toFixed(this.decimalPlaces);
        }

        return parseFloat(max);
    }

    get template() {
        let template = this.input.getAttribute('template');

        if (template) {
            return template;
        }

        template = '{{value}}';

        if (this.append) {
            template = `${template} {{append}}`;
        }

        return template;
    }

    get append() {
        return this.input.getAttribute('append');
    }

    get value() {
        let value = this.input.value.toString().replace(',', '.')

        return value || this.min;
    }

    set value(newValue) {
        newValue = parseFloat(newValue)

        if (isNaN(newValue)) {
            newValue = this.min
        }

        if (!this.isInputFocused && this.template) {
            newValue = this.template
                .replace('{{value}}', newValue)
                .replace('{{append}}', this.append);
        }

        this.input.value = newValue;
        this.input.setAttribute('value', newValue);
    }

    get hasChangeCallback() {
        return this.input.onchange != null;
    }

    constructor() {
        super();

        this.spinning = false;
        this.spinStartDelay = 450;
        this.spinIncrementDelay = 65;
        this.spinStartTimeout = null;
        this.spinNextTime = 0;
        this.spinValue = 0;
        this.oldValue = null;
        this.spinningOldValue = null;
        this.spinDirection = null;
        this.isInputFocused = false;

        this.increment = this.increment.bind(this)
        this.decrement = this.decrement.bind(this)
        this.stopSpinning = this.stopSpinning.bind(this)
        this.stopSpinning = this.stopSpinning.bind(this)
        this.inputFocused = this.inputFocused.bind(this)
        this.inputKeydown = this.inputKeydown.bind(this)
        this.inputBlurred = this.inputBlurred.bind(this)
    }

    connectedCallback() {
        requestAnimationFrame(() => {
            this.input = this.querySelector('input');
            this.incrementBtn = this.querySelector('[increment]');
            this.decrementBtn = this.querySelector('[decrement]');

            if (this.incrementBtn) {
                this.incrementBtn.addEventListener('mousedown', this.increment);
                this.incrementBtn.addEventListener('mouseup', this.stopSpinning);
                this.incrementBtn.addEventListener('mouseleave', this.stopSpinning);
            }

            if (this.decrementBtn) {
                this.decrementBtn.addEventListener('mousedown', this.decrement);
                this.decrementBtn.addEventListener('mouseup', this.stopSpinning);
                this.decrementBtn.addEventListener('mouseleave', this.stopSpinning);
            }

            if (this.input) {
                this.input.addEventListener('focus', this.inputFocused);
                this.input.addEventListener('keydown', this.inputKeydown);
                this.input.addEventListener('blur', this.inputBlurred);
            }

            this.oldValue = this.value
            this.updateQuantity(this.value, false);

            this.classList.add('is-loaded');

            this.dispatchSpinnerEvent('created');
        })
    }

    getParsedValue() {
        return parseFloat(this.value);
    }

    getParsedOldValue() {
        return parseFloat(this.oldValue);
    }

    getParsedSpinningOldValue() {
        return parseFloat(this.spinningOldValue);
    }

    increment() {
        this.spinValue = this.step;
        this.spinDirection = 'up';

        this.startSpinning();
    }

    decrement() {
        this.spinValue = this.step * -1;
        this.spinDirection = 'down';

        this.startSpinning();
    }

    inputFocused() {
        this.oldValue = this.value
        this.spinDirection = null

        this.isInputFocused = true
        this.value = this.valueOrMin;

        this.input.select();
    }

    inputKeydown(event) {
        if (event.keyCode === 13) {
            this.input.blur();
        }
    }

    inputBlurred() {
        this.isInputFocused = false
        this.updateQuantity(this.value);
    }

    startSpinning() {
        this.dispatchSpinnerEvent('start');
        this.oldValue = this.value
        this.spinningOldValue = null

        this.spinning = true;
        this.updateQuantity();

        if (this.spinStartTimeout) {
            clearTimeout(this.spinStartTimeout);
        }

        this.spinStartTimeout = setTimeout(() => {
            requestAnimationFrame(this.spin.bind(this));

            this.spinStartTimeout = null;
        }, this.spinStartDelay);
    }

    stopSpinning() {
        if (!this.spinning) {
            return;
        }

        this.spinning = false;
        this.spinningOldValue = null;

        this.dispatchSpinnerEvent('stop');
        this.updateQuantity(this.value, false)

        if (this.hasChangeCallback) {
            this.input.onchange();
        };
    }

    spin(time) {
        if (!this.spinning) {
            return;
        }

        requestAnimationFrame(this.spin.bind(this));

        if (time < this.spinNextTime) {
            return;
        }

        this.spinNextTime = time + this.spinIncrementDelay;

        this.spinningOldValue = this.value;
        this.updateQuantity();
    }

    updateQuantity(value = null, triggerEvent = true) {
        let newValue = this.valueOrMin + this.spinValue;

        if (value !== null) {
            newValue = parseFloat(value);
        }

        this.value = this.parseValue(newValue);

        if (!this.spinning && this.getParsedOldValue() === parseFloat(newValue)) {
            triggerEvent = false;
        }

        if (triggerEvent) {
            this.dispatchSpinnerEvent((this.spinning ? 'spin' : 'changed'));

            if (!this.spinning && this.hasChangeCallback) {
                this.input.onchange();
            }
        }
    }

    parseValue(value) {
        value = parseFloat(value);

        if (value < this.min) {
            value = this.min;
        }

        if (value > this.max) {
            value = this.max;
        }

        if (this.decimalPlaces || this.step !== 1) {
            value = Math.round(value / this.step) * this.step;
        }

        return parseFloat(value).toFixed(this.decimalPlaces);
    }

    dispatchSpinnerEvent(eventName, eventData = {}) {
        this.dispatchEvent(new CustomEvent(eventName, {
            detail: Object.assign({
                rSpinner: this
            }, eventData)
        }));
    }

    disconnectedCallback() {
        if (this.incrementBtn) {
            this.incrementBtn.removeEventListener('mousedown', this.increment);
            this.incrementBtn.removeEventListener('mouseup', this.stopSpinning);
            this.incrementBtn.removeEventListener('mouseleave', this.stopSpinning);
        }

        if (this.decrementBtn) {
            this.decrementBtn.removeEventListener('mousedown', this.decrement);
            this.decrementBtn.removeEventListener('mouseup', this.stopSpinning);
            this.decrementBtn.removeEventListener('mouseleave', this.stopSpinning);
        }

        if (this.input) {
            this.input.removeEventListener('focus', this.inputFocused);
            this.input.removeEventListener('keydown', this.inputKeydown);
            this.input.removeEventListener('blur', this.inputBlurred);
        }
    }
}

customElements.define('r-spinner', rSpinner);
